import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Scene from 'threejs_scene/Scene';
import Light from 'threejs_scene/Light';
import { preloadObject, preloadSceneTexture } from 'threejs_scene/sceneUtils';
import { isAndroid, isMobile, isMobileOnly } from 'react-device-detect';
import { createSelector } from '@reduxjs/toolkit';
import SoundHotspotController from './SoundHotspotController';
import EntranceVideo from './EntranceVideo';
import { formURL } from '@wsc/utils/apiUtils';
import { setModalProps } from '@wsc/redux_stores/modalsReducer';
import RoomObjects from './RoomObjects';
import { fetchRoomObjectsBySceneId } from '@wsc/redux_stores/roomObjectsReducer';
import {
	setActiveScene,
	setRenderUI,
	setSceneBgLoaded,
} from '@wsc/redux_stores/sceneLoadReducer';
import {
	checkIfURLContainsSearchParam,
	get3DModelUrl,
	getBustKey,
} from '@wsc/utils/urlHelpers';
import { getSceneCubeMapDir, getSceneType } from '@wsc/utils/sceneUtils';
import InSceneComponentsLoader from '@wsc/components/loaders/InSceneComponentsLoader';
import { useAnalytics, useLocalize, useXR } from '@wsc/hooks';
import './room.scss';
import AccessibilityListener from '@wsc/components/Room/AccessibilityListener';
import ConnectedScenesPreLoader from '@wsc/components/Room/ConnectedScenesPreLoader';
import LinkedHotspotController from '@wsc/components/Room/LinkedHotspotController';
import InSceneTextController from '@wsc/components/Room/InSceneTextController';
import MediaManager from '@wsc/components/Room/MediaManager';
import { criticalMessage } from '@wsc/utils/consoleHelpers';
import { clearRoomObjects } from '@wsc/redux_stores/roomObjectsReducer';
import PerformanceMonitor from '@wsc/components/PerformanceMonitor';
import { setAllHotspotsLoaded } from '@wsc/redux_stores/sceneLoadReducer';

const selectRoomObjects = (state) => state.roomObjects;

const creatorToolsEnabledSelector = createSelector(
	[(state) => state.creatorTools],
	(creatorTools) => {
		const { isEnabled, autoPan, panSpeed, panDirection } = creatorTools;
		return {
			isEnabled,
			autoPan,
			panSpeed,
			panDirection,
		};
	},
);

const selectCurrentAvatar = createSelector(
	[(state) => state.storeAvatars.selectedAvatar],
	(selectedAvatar) => selectedAvatar,
);

const storeDataSelector = createSelector(
	[
		(state) => state.storeData.avatar_configuration,
		(state) => state.storeData.scene_configuration,
		(state) => state.storeData.styling,
		(state) => state.storeData.id,
		(state) => state.storeData.hotspots_configuration?.proximity,
		(state) => state.storeData.general.monitor_performance,
	],
	(
		avatar_configuration,
		scene_configuration,
		styling,
		storeId,
		proximityConfig,
		enablePerformanceMonitoring,
	) => {
		return {
			avatarConfiguration: avatar_configuration,
			sceneConfiguration: scene_configuration,
			primaryColor: styling?.primary_color || 'black',
			secondaryColor: styling?.secondary_color || 'white',
			stylingIcons: styling?.icons || {},
			storeIconFiles: styling?.store_icon_files || {},
			storeId,
			proximityConfig: proximityConfig || null,
			enablePerformanceMonitoring:
				enablePerformanceMonitoring !== false
		};
	},
);

const selectInSceneComponents = createSelector(
	[(state) => state?.componentConfig],
	(componentConfig) => {
		const { inSceneComponents, tooltips } = componentConfig || {};
		return { inSceneComponents, tooltips };
	},
);

const createSceneDataSelector = (sceneId) =>
	createSelector([(state) => state.scenes], (scenes) => {
		return scenes[sceneId];
	});

const Room = ({ sceneId, webpSupport }) => {
	criticalMessage(
		"Room re-rendered -> If you are seeing too many of these or these aren't stopping, you are doing something wrong, check your state updates, redux subscriptions or anything causing the re-renders. Known numbers: 3-5 each scene. ",
	);

	const selectSceneData = useMemo(
		() => createSceneDataSelector(sceneId),
		[sceneId],
	);

	const sceneData = useSelector(selectSceneData);
	const dispatch = useDispatch();

	const {
		avatarConfiguration,
		sceneConfiguration,
		primaryColor,
		secondaryColor,
		stylingIcons,
		storeIconFiles,
		storeId,
		proximityConfig,
		enablePerformanceMonitoring,
	} = useSelector(storeDataSelector);

	// eslint-disable-next-line camelcase

	const selectedAvatar = useSelector(selectCurrentAvatar);

	const roomObjects = useSelector(selectRoomObjects);
	const { tooltips = [], inSceneComponents: inSceneComponentsConfig = [] } =
		useSelector(selectInSceneComponents);

	const {
		isEnabled: creatorToolsEnabled,
		autoPan,
		panSpeed,
		panDirection,
	} = useSelector(creatorToolsEnabledSelector);

	const {
		handleXRSessionEnd,
		isXREnabled,
		xrSessionType,
		xrOptionalFeatures,
	} = useXR();

	const tooltipComponents = InSceneComponentsLoader({ components: tooltips });
	const inSceneComponents = InSceneComponentsLoader({
		components: inSceneComponentsConfig,
	});

	const { activeLocale, enabled: localesEnabled } = useLocalize();

	const abortControl = new AbortController();
	const flatSceneUrl =
		isMobile && sceneData?.mobile_flat_scene_url
			? sceneData?.mobile_flat_scene_url
			: sceneData?.flat_scene_url;

	const opacityMapUrl = sceneData?.opacity_map || null;

	const entranceVideoUrl = isMobile
		? sceneData?.mobile_first_scene_video_url
		: sceneData?.first_scene_video_url;

	const [showEntranceVideo, setShowEntranceVideo] = useState(false);

	const sceneType = useMemo(() => getSceneType(sceneData), [sceneData]);

	const getEntranceVideo = () => {
		const videoRecord = sessionStorage.getItem('entranceVideoRecord') || [];
		if (
			entranceVideoUrl &&
			!checkIfURLContainsSearchParam('linkHsId') &&
			(!sceneData.display_video_once_per_session ||
				!videoRecord.includes(sceneData.id))
		) {
			setShowEntranceVideo(true);
			if (!videoRecord.includes(sceneData.id)) {
				try {
					sessionStorage.setItem('entranceVideoRecord', [
						...videoRecord,
						sceneData.id,
					]);
				} catch (e) {
					console.warn("Can't set entrance video record");
				}
			}
		} else {
			setShowEntranceVideo(false);
		}
	};

	const initNewScene = () => {
		getEntranceVideo();
		dispatch(
			fetchRoomObjectsBySceneId({ sceneId, locale: activeLocale }),
		).then(() => {
			dispatch(setActiveScene(sceneData.id));
		});
	};

	const cleanup = () => {
		dispatch(clearRoomObjects());

		// setSceneBGLoaded(false);
		dispatch(setSceneBgLoaded(false));
	};

	useEffect(() => {
		dispatch(setAllHotspotsLoaded(false));
		initNewScene();

		return () => {
			abortControl.abort();
			cleanup();
		};
	}, [sceneData.id]);
	const { collect } = useAnalytics();

	useEffect(() => {
		if (sceneData?.id) {
			collect({
				scene_name: sceneData?.name,
				page_view: true,
			});
		}
	}, [sceneData?.id]);

	const getBackgroundUrl = () => {
		if (sceneType === 'cubemap_scene') {
			return getSceneCubeMapDir(sceneData, localesEnabled, activeLocale);
		}
		return formURL(flatSceneUrl);
	};

	const getVideoName = () => {
		if (isAndroid) {
			return 'processed_1k';
		}
		return 'processed_2k';
	};

	//TODO: renderer_config belong to Renderer instance and not to scene background.
	const bgConfig = useMemo(() => {
		const config = {
			isFlatScene: !!sceneData.flat_scene_url,
			backgroundUrl: getBackgroundUrl(),
			opacityMapUrl: opacityMapUrl && formURL(opacityMapUrl),
			imageIntegrity: getBustKey(sceneData),
			useWebp: webpSupport,
			skipLargest: isMobile,
			materialProperties: sceneData?.material_properties || {},
			sceneType,
			...(sceneData.scene_model
				? {
						scene_model: {
							...sceneData?.scene_model,
							url: get3DModelUrl(sceneData),
							primary_color: primaryColor,
							secondary_color: secondaryColor,
						},
				  }
				: {}),
			_3dModelURL: get3DModelUrl(sceneData),
			...(sceneData?.environment_map_config?.url
				? {
						environmentMapURL: formURL(
							sceneData?.environment_map_config?.url,
						),
				  }
				: {}),
			...(sceneData?.environment_map_config?.use_scene_bg_images &&
			!sceneData?.environment_map_config?.url
				? {
						use_scene_background_as_environment_map:
							sceneData.environment_map_config
								.use_scene_bg_images,
				  }
				: {}),
			glb_model_position: sceneData?.glb_model_position,
			envMapIntensity:
				sceneData?.environment_map_config?.intensity || 1.0,
			toneMapping: sceneData?.renderer_config?.tone_mapping || 'no',
			toneMappingExposure:
				sceneData?.renderer_config?.tone_mapping_exposure || 1.0,
			videoBackground:
				sceneData?.video_background_dir &&
				`${formURL(
					sceneData.video_background_dir,
				)}${getVideoName()}.mp4`,
			videoBackgroundOptions: sceneData?.video_background_options
				?.caption_file
				? {
						...sceneData?.video_background_options,
						...{
							caption_file: formURL(
								sceneData?.video_background_options
									.caption_file,
							),
						},
				  }
				: sceneData?.video_background_options,
		};

		Object.keys(roomObjects).forEach((key) => {
			if (roomObjects[key]?.props?.hotspot_type === '3d_model') {
				config.materialProperties = {
					...config.materialProperties,
					depth_test: true,
				};
			}
		});

		if (['3d_model_scene', '3d_scene'].includes(sceneType)) {
			config.backgroundUrl =
				sceneData?.scene_background &&
				formURL(sceneData.scene_background);
		}
		return config;
	}, [
		webpSupport,
		sceneType,
		sceneData,
		primaryColor,
		secondaryColor,
		roomObjects,
	]);

	const getSceneTransitionIcon = () => {
		if (
			'transition_icon' in stylingIcons &&
			stylingIcons.transition_icon.name in storeIconFiles
		) {
			return formURL(
				storeIconFiles[stylingIcons.transition_icon.name].url,
			);
		}
		return null;
	};

	const onEntranceVideoPlaying = () => {
		/**
		 * Preload current scene assets while entrance video is playing
		 * **/
		if (sceneType === '3d_model_scene') {
			bgConfig?.scene_model?.url &&
				preloadObject(bgConfig.scene_model.url, abortControl);
			bgConfig?.environmentMapURL &&
				preloadSceneTexture(bgConfig.environmentMapURL, abortControl);
			bgConfig?.backgroundUrl &&
				preloadSceneTexture(bgConfig.backgroundUrl, abortControl);
			selectedAvatar?.glbUrl &&
				preloadObject(selectedAvatar.glbUrl, abortControl);
		}
	};

	const onVideoEnd = () => {
		setShowEntranceVideo(false);
	};

	const onBgLoaded = () => {
		dispatch(setSceneBgLoaded(true));
		// setSceneBGLoaded(true);
	};

	const onBGReady = () => {
		dispatch(setRenderUI(true));
	};

	const onSceneError = (e) => {
		dispatch(setRenderUI(true));
		dispatch(
			setModalProps({
				key: 'welcome_modal',
				props: {
					visible: false,
				},
			}),
		);
		dispatch(
			setModalProps({
				key: 'scene_error',
				props: {
					error: e,
					visible: true,
				},
			}),
		);
	};

	const autoRotateConfig = useMemo(() => {
		const autoRotateConfig = {
			enabled: false,
			cancelAutoRotateOnInteraction: true,
			activateOnEachSceneLoad: false,
		};
		const panConfig = sceneConfiguration?.pan || {};

		if (bgConfig.isFlatScene) {
			return autoRotateConfig;
		}

		if (panConfig.enable) {
			autoRotateConfig.enabled = true;
			autoRotateConfig.autoRotateSpeed = panConfig?.speed || 1.0;
			autoRotateConfig.autoRotateDelay = panConfig?.inactivity_timer || 0;
			autoRotateConfig.autoRotateDirection =
				panConfig?.direction || 'left';
			autoRotateConfig.cancelAutoRotateOnInteraction = true;
			autoRotateConfig.activateOnEachSceneLoad =
				panConfig?.activate_on_each_scene_load || false;
			autoRotateConfig.resetSceneOnInteraction =
				panConfig?.reset_scene_on_interaction == false ? false : true;
		}

		if (creatorToolsEnabled) {
			autoRotateConfig.enabled = autoPan;
			autoRotateConfig.autoRotateSpeed = panSpeed;
			autoRotateConfig.autoRotateDelay = 0;
			autoRotateConfig.autoRotateDirection = panDirection;
			autoRotateConfig.cancelAutoRotateOnInteraction = false;
		}

		return autoRotateConfig;
	}, [
		creatorToolsEnabled,
		sceneConfiguration?.pan,
		bgConfig.isFlatScene,
		autoPan,
		panSpeed,
		panDirection,
	]);

	const getControlMode = () => {
		if (sceneConfiguration?.orbit_controls?.navigation_type) {
			return sceneConfiguration.orbit_controls.navigation_type;
		}
		return selectedAvatar === null || selectedAvatar === undefined
			? 'first_person_navigation'
			: 'avatar_navigation';
	};

	// Note: If you are trying to find why the entire UI lods twice initially, it is here.
	// Layout is rendered twice causing all the other elements to re-render.
	return sceneData ? (
		<>
			{(!showEntranceVideo || (showEntranceVideo && isXREnabled)) && (
				<Scene
					sceneId={sceneData.id}
					sceneType={sceneData?.scene_type}
					bgConf={bgConfig}
					renderer_config={sceneData?.renderer_config}
					transition={sceneConfiguration?.transition}
					allowHotspotsToMove={false}
					enablePan={bgConfig?.isFlatScene}
					type="containerInstance"
					orbitControlsConfig={sceneData?.controls}
					autoRotateConfig={autoRotateConfig}
					cameraProperties={sceneData?.camera?.camera_properties}
					fov={sceneData?.camera?.fov}
					loadingIconSrc={getSceneTransitionIcon()}
					onBackgroundLoaded={onBgLoaded}
					onBackgroundReady={onBGReady}
					onSceneError={onSceneError}
					tooltipComponents={tooltipComponents}
					inSceneComponents={inSceneComponents}
					primaryColor={primaryColor}
					secondaryColor={secondaryColor}
					dampingFactors={{
						enable: sceneConfiguration?.damping?.enable || false,
						factor: sceneConfiguration?.damping?.factor || 0.1,
					}}
					rotateCameraTowardsMovementDirection={
						sceneConfiguration?.orbit_controls?.rotate_camera_towards_movement_direction
					}
					selectedAvatar={selectedAvatar}
					sceneOrbitControlsConfig={{
						...sceneData?.avatar?.orbit_controls,
					}}
					controlMode={getControlMode()}
					avatarConfiguration={{
						...avatarConfiguration,
						...sceneData?.avatar,
						onClick: () => {
							window.dispatchEvent(
								new CustomEvent('onAvatarClicked'),
							);
						},
					}}
					allHotspotsLoaded={() => {
						dispatch(setAllHotspotsLoaded(true));
					}}
					isXREnabled={isXREnabled}
					xrSessionType={xrSessionType}
					xrOptionalFeatures={xrOptionalFeatures}
					handleXRSessionEnd={handleXRSessionEnd}
					entranceVideoUrl={formURL(entranceVideoUrl)}
					showEntranceVideo={showEntranceVideo}
					onEntranceVideoEnd={onVideoEnd}
					proximityConfig={proximityConfig}
				>
					<RoomObjects sceneId={sceneData.id} />

					{/*  LIGHT  */}
					{[
						sceneData?.ambient_light?.enabled ? (
							<Light
								key="ambient"
								type="ambient"
								config={sceneData.ambient_light}
							/>
						) : (
							false
						),
						sceneData?.directional_light?.enabled ? (
							<Light
								key="directional"
								type="directional"
								config={sceneData.directional_light}
							/>
						) : (
							false
						),
					]}
				</Scene>
			)}
			<SoundHotspotController />
			<AccessibilityListener />
			<MediaManager />
			<ConnectedScenesPreLoader webpSupport={webpSupport} />
			<LinkedHotspotController />
			<InSceneTextController />
			{enablePerformanceMonitoring && (
				<PerformanceMonitor storeId={storeId} />
			)}
			{showEntranceVideo && !isXREnabled && (
				<EntranceVideo
					videoUrl={formURL(entranceVideoUrl)}
					onVideoEnd={onVideoEnd}
					canPlayThrough={onEntranceVideoPlaying}
					onClose={() => {
						setShowEntranceVideo(false);
						collect({
							event_name: 'click_misc',
							action: 'Store intro skipped',
						});
					}}
				/>
			)}
		</>
	) : null;
};

Room.propTypes = {
	sceneId: PropTypes.string.isRequired,
	webpSupport: PropTypes.bool,
};

Room.defaultProps = {
	webpSupport: true,
};
export default Room;
